import {
  ZodArray,
  ZodDiscriminatedUnion,
  ZodEffects,
  ZodNullable,
  ZodObject,
  ZodOptional,
  ZodTypeAny,
  ZodUnion,
} from 'zod';

import { Logger } from '@rover/rsdk/src/modules/Logging';

/**
 * Determines if a key is required in a Zod schema
 * @param zodSchema The Zod schema to check
 * @param key The key to check
 * @returns Whether the key is required
 */
export const getRequired = (zodSchema: ZodTypeAny, key: string): boolean => {
  try {
    // Recursively traverse the schema
    const traverse = (schema: ZodTypeAny, path: string[]): boolean => {
      if (path.length === 0) {
        // Base case: Check if the schema is optional or nullable
        return !(schema instanceof ZodOptional || schema instanceof ZodNullable);
      }

      const [currentKey, ...restKeys] = path;

      if (schema instanceof ZodObject) {
        const { shape } = schema;
        if (!(currentKey in shape)) {
          throw new Error(`Key "${currentKey}" does not exist in schema`);
        }
        return traverse(shape[currentKey], restKeys);
      }

      if (schema instanceof ZodEffects) {
        return traverse(schema._def.schema, path); // eslint-disable-line no-underscore-dangle
      }

      if (schema instanceof ZodArray) {
        return traverse(schema.element, restKeys);
      }

      if (schema instanceof ZodDiscriminatedUnion || schema instanceof ZodUnion) {
        return schema._def.options.some((option) => traverse(option, path)); // eslint-disable-line no-underscore-dangle
      }

      throw new Error(`Unsupported schema type at key "${currentKey}"`);
    };

    return traverse(zodSchema, key.split('.'));
  } catch (e) {
    if (e instanceof Error) {
      Logger.error(e);
    }
    return false;
  }
};
